import React from 'react';
import { X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { AppSelect } from '../../../components/AppSelect';
import { AutoResizeTextarea } from '../../../components/AutoResizeTextarea';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { OFFER_UNIT_OPTIONS } from '../../../constants/offerUnits';

const FIELD_CLASS =
  'w-full h-9 px-3 py-0 border border-app-border rounded-md text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none';

export type LeistungCreateModalProps = {
  open: boolean;
  onClose: () => void;
  saving: boolean;
  previewVkLabel: string;
  error?: string | null;
  name: string;
  articleNumber: string;
  unit: string;
  kosten: string;
  surcharge: string;
  description: string;
  onNameChange: (v: string) => void;
  onArticleNumberChange: (v: string) => void;
  onUnitChange: (v: string) => void;
  onKostenChange: (v: string) => void;
  onSurchargeChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
};

export function LeistungCreateModal({
  open,
  onClose,
  saving,
  previewVkLabel,
  error,
  name,
  articleNumber,
  unit,
  kosten,
  surcharge,
  description,
  onNameChange,
  onArticleNumberChange,
  onUnitChange,
  onKostenChange,
  onSurchargeChange,
  onDescriptionChange,
  onSubmit,
}: LeistungCreateModalProps) {
  const canSubmit = !saving && Boolean(name.trim() && unit.trim() && kosten.trim());

  return (
    <AppModal
      open={open}
      onClose={onClose}
      disableClose={saving}
      overlayClassName="z-50"
      panelClassName="md:max-w-2xl"
      ariaLabelledBy="leistung-create-title"
    >
      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-app-border px-5 py-3">
          <h2 id="leistung-create-title" className="text-sm font-semibold text-app-text">
            Neue Leistung
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error ? (
          <div className="mx-5 mt-3 shrink-0 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="space-y-4 overflow-y-auto p-5">
          <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                Name<span className="text-red-500">*</span>
              </label>
              <input
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z. B. Montagearbeit"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">Leistungsnummer</label>
              <input
                value={articleNumber}
                onChange={(e) => onArticleNumberChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z. B. L-1001"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                Einheit<span className="text-red-500">*</span>
              </label>
              <AppSelect
                value={unit}
                onChange={(v) => onUnitChange(v || '')}
                options={OFFER_UNIT_OPTIONS}
                size="sm"
                triggerClassName="!min-h-0 h-9 !py-0"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">
                Kosten<span className="text-red-500">*</span>
              </label>
              <input
                value={kosten}
                onChange={(e) => onKostenChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z. B. 120,00"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-app-text-muted">Zuschlag (%)</label>
              <input
                type="text"
                inputMode="decimal"
                value={surcharge}
                onChange={(e) => onSurchargeChange(e.target.value)}
                className={FIELD_CLASS}
                placeholder="z. B. 25 (leer = 0 %)"
              />
            </div>
            <div className="rounded-md border border-app-border bg-app-surface-muted px-3 py-2 text-sm text-app-text-muted md:col-span-2">
              <span className="font-medium text-app-text-muted">VK (berechnet):</span>{' '}
              <span className="tabular-nums">{previewVkLabel}</span>
              <span className="ml-2 text-xs text-app-text-subtle">Kosten × (1 + Zuschlag % / 100)</span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-app-text-muted">Beschreibung</label>
            <AutoResizeTextarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              rows={3}
              maxHeight={240}
              className="min-h-[72px] w-full resize-none rounded-md border border-app-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
              placeholder="Optionale Beschreibung…"
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-app-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-9 items-center justify-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-pipedrive-blue px-3.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {saving ? <LoadingSpinner size="sm" /> : null}
            Leistung anlegen
          </button>
        </div>
      </form>
    </AppModal>
  );
}
